import { z } from "zod";
import { dec, formatearMoneda, redondear } from "../money.js";
import { CLAVES } from "../sat/catalogos.js";
/**
 * Dominios de internet que administramos para los clientes.
 *
 * Se llama dominio-web.ts y no dominio.ts porque "dominio" ya es el nombre
 * de la carpeta (el dominio del negocio) y los imports se vuelven ilegibles.
 */
/** Cuándo avisar al cliente, en días antes del vencimiento. */
export const DIAS_AVISO_DEFAULT = [60, 30, 15, 7, 1];
/**
 * IVA que se le suma al costo de renovación en los avisos. El costo que se
 * captura en el sistema es SIN IVA, que es como lo cotiza el proveedor y como
 * se factura; el cliente ve el total que de verdad va a pagar.
 */
export const TASA_IVA = "0.16";
/** Desglose de un costo sin IVA. Todo en cadenas con 2 decimales. */
export function desglosarIva(costoSinIva) {
    const subtotal = redondear(costoSinIva, 2);
    const iva = redondear(dec(subtotal).times(TASA_IVA), 2);
    return { subtotal, iva, total: redondear(dec(subtotal).plus(iva), 2) };
}
/** Debajo de este margen un dominio se considera "por vencer" en el admin. */
export const DIAS_POR_VENCER = 60;
const nombreDominio = z
    .string()
    .trim()
    .toLowerCase()
    // Quien pega "https://www.acme.com/" desde el navegador no debería ver un
    // error: se limpia el protocolo y la ruta, y se valida lo que queda.
    .transform((v) => v.replace(/^[a-z]+:\/\//, "").replace(/\/.*$/, ""))
    .pipe(z
    .string()
    .max(253)
    .regex(/^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9-]{2,63}$/, "dominio inválido, p.ej. acme.com.mx"));
/** Acepta "" para poder vaciar el campo desde un formulario de edición. */
const monto = z
    .union([z.literal(""), z.string().regex(/^\d+(\.\d{1,2})?$/, "monto inválido")])
    .optional();
const fechaOpcional = z.union([z.literal(""), z.iso.date()]).optional();
const correos = z
    .string()
    .trim()
    .max(500)
    .refine((v) => v === "" ||
    v.split(",").every((c) => z.email().safeParse(c.trim()).success), "uno o más correos no son válidos (sepáralos con coma)")
    .optional();
export const dominioCreateSchema = z.object({
    clienteId: z.uuid(),
    proveedorId: z.uuid().optional(),
    nombre: nombreDominio,
    fechaRegistro: fechaOpcional,
    fechaVencimiento: z.iso.date(),
    autoRenovacion: z.boolean().default(false),
    costoRenovacion: monto,
    moneda: z.enum(CLAVES.moneda).default("MXN"),
    cuentaProveedor: z
        .string()
        .trim()
        .max(200)
        .optional()
        .describe("Usuario o correo de la cuenta en el proveedor. Nunca la contraseña."),
    recordatoriosActivos: z.boolean().default(true),
    diasAviso: z
        .array(z.number().int().min(0).max(365))
        .min(1, "al menos un aviso")
        .max(10)
        .default([...DIAS_AVISO_DEFAULT]),
    correosAviso: correos.describe("Si se llena, los avisos van aquí en lugar de a los contactos del cliente"),
    notas: z.string().max(4000).optional(),
});
export const dominioUpdateSchema = dominioCreateSchema
    .partial()
    .omit({ clienteId: true })
    // null = quitar el proveedor asignado.
    .extend({ activo: z.boolean().optional(), proveedorId: z.uuid().nullable().optional() });
export const renovarDominioSchema = z.object({
    fechaVencimiento: z.iso.date(),
    costoRenovacion: monto,
    nota: z.string().trim().max(500).optional(),
});
export const VENCIMIENTOS = ["todos", "vencidos", "por_vencer", "vigentes"];
export const listaDominiosQuerySchema = z.object({
    clienteId: z.uuid().optional(),
    proveedorId: z.uuid().optional(),
    vencimiento: z.enum(VENCIMIENTOS).default("todos"),
    /** Umbral de "por vencer" en días. */
    dias: z.coerce.number().int().min(1).max(365).default(DIAS_POR_VENCER),
    /** "true" (default) solo los que seguimos administrando. */
    activo: z.enum(["true", "false", "todos"]).default("true"),
    q: z.string().trim().optional(),
    limite: z.coerce.number().int().min(1).max(500).default(200),
});
// --- Proveedores (registrars) ----------------------------------------------
export const proveedorDominioCreateSchema = z.object({
    nombre: z.string().trim().min(1).max(120),
    sitioWeb: z.string().trim().max(300).optional(),
    notas: z.string().max(2000).optional(),
});
export const proveedorDominioUpdateSchema = proveedorDominioCreateSchema.partial();
// --- Fechas y avisos ---------------------------------------------------------
/**
 * Hoy en la zona del negocio, como YYYY-MM-DD.
 *
 * `toISOString()` daría la fecha en UTC: de las 6 pm en adelante en México ya
 * es "mañana", y un dominio que vence mañana aparecería como que vence hoy.
 */
export function hoyMexico(ahora = new Date()) {
    return new Intl.DateTimeFormat("en-CA", {
        timeZone: "America/Mexico_City",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).format(ahora);
}
const MS_DIA = 86_400_000;
/** Suma días a una fecha YYYY-MM-DD sin pasar por zonas horarias. */
export function sumarDias(fecha, dias) {
    const t = Date.parse(`${fecha}T00:00:00Z`) + dias * MS_DIA;
    return new Date(t).toISOString().slice(0, 10);
}
/** Días que faltan para `fechaVencimiento`. Negativo = ya venció. */
export function diasParaVencer(fechaVencimiento, hoy = hoyMexico()) {
    return Math.round((Date.parse(`${fechaVencimiento}T00:00:00Z`) - Date.parse(`${hoy}T00:00:00Z`)) / MS_DIA);
}
/** Un año después, cuidando el 29 de febrero (el 29/feb pasa a 28/feb). */
export function sumarAnios(fecha, anios) {
    const [a, m, d] = fecha.split("-").map(Number);
    const destino = new Date(Date.UTC(a + anios, m - 1, d));
    if (destino.getUTCMonth() !== m - 1)
        destino.setUTCDate(0);
    return destino.toISOString().slice(0, 10);
}
/**
 * ¿Qué aviso toca mandar hoy? Devuelve el umbral, o null si ninguno.
 *
 * Regla: el umbral más cercano al vencimiento que ya se alcanzó
 * (diasRestantes <= umbral) y que todavía no se mandó en este ciclo.
 * Mandar solo uno aunque se hayan cruzado varios evita el correo triple
 * cuando se da de alta un dominio que vence en 10 días con avisos a 60, 30
 * y 15: el cliente recibe uno, no tres.
 */
export function avisoPendiente(diasRestantes, diasAviso, yaEnviados) {
    const alcanzados = diasAviso.filter((d) => diasRestantes <= d);
    if (alcanzados.length === 0)
        return null;
    const masCercano = Math.min(...alcanzados);
    return yaEnviados.includes(masCercano) ? null : masCercano;
}
const FORMATO_FECHA = new Intl.DateTimeFormat("es-MX", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
});
export const fechaLarga = (iso) => FORMATO_FECHA.format(new Date(`${iso}T00:00:00Z`));
/**
 * El recordatorio de vencimiento. Vive aquí, no en la API, para que el correo
 * automático y el botón "Redactar correo" del admin digan exactamente lo mismo.
 */
export function redactarRecordatorioDominio(args) {
    const { dominio, fechaVencimiento, diasRestantes, firma } = args;
    const fecha = fechaLarga(fechaVencimiento);
    const plural = (n) => (n === 1 ? "" : "s");
    let asunto;
    let titulo;
    let situacion;
    let cuando;
    let urgencia;
    if (diasRestantes < 0) {
        const n = Math.abs(diasRestantes);
        asunto = `Tu dominio ${dominio} ya venció`;
        titulo = "Tu dominio ya venció";
        cuando = `Venció hace ${n} día${plural(n)}`;
        urgencia = "vencida";
        situacion = `El dominio ${dominio} venció el ${fecha}, hace ${n} día${plural(n)}. Si no se renueva pronto, el sitio y los correos que dependen de él pueden dejar de funcionar y el dominio podría quedar disponible para otra persona.`;
    }
    else if (diasRestantes === 0) {
        asunto = `Tu dominio ${dominio} vence hoy`;
        titulo = "Tu dominio vence hoy";
        cuando = "Vence hoy";
        urgencia = "vencida";
        situacion = `El dominio ${dominio} vence hoy, ${fecha}. Para que tu sitio y tus correos sigan funcionando hay que renovarlo de inmediato.`;
    }
    else {
        asunto = `Tu dominio ${dominio} vence el ${fecha}`;
        titulo = "Tu dominio está por vencer";
        cuando = `En ${diasRestantes} día${plural(diasRestantes)}`;
        urgencia = diasRestantes <= 15 ? "proxima" : "normal";
        situacion = `El dominio ${dominio} vence el ${fecha} (en ${diasRestantes} día${plural(diasRestantes)}). Para que tu sitio y tus correos sigan funcionando sin interrupciones, conviene renovarlo antes de esa fecha.`;
    }
    const desglose = args.costoRenovacion && Number(args.costoRenovacion) > 0
        ? desglosarIva(args.costoRenovacion)
        : null;
    const moneda = args.moneda ?? "MXN";
    // En pesos se aclara "MXN"; las demás monedas ya traen su código ("USD 450.00").
    const dinero = (v) => moneda === "MXN" ? `${formatearMoneda(v, moneda)} MXN` : formatearMoneda(v, moneda);
    const datos = [
        { etiqueta: "Dominio", valor: dominio },
        { etiqueta: "Vencimiento", valor: fecha },
        { etiqueta: "Tiempo restante", valor: cuando },
    ];
    if (desglose) {
        datos.push({ etiqueta: "Costo de renovación", valor: dinero(desglose.subtotal) }, { etiqueta: "IVA (16%)", valor: dinero(desglose.iva) }, { etiqueta: "Total a pagar", valor: dinero(desglose.total) });
    }
    const pideConfirmacion = !args.autoRenovacion;
    const indicacion = args.autoRenovacion
        ? "El dominio tiene renovación automática, así que si el cargo se aplica no tienes que hacer nada; este aviso es solo para que lo tengas presente."
        : "Responde a este correo para confirmar la renovación y nos encargamos del trámite.";
    const lineas = ["Hola,", "", situacion];
    if (desglose) {
        lineas.push("", `Costo de la renovación: ${dinero(desglose.subtotal)} + IVA (16%) ${dinero(desglose.iva)} = ${dinero(desglose.total)}.`);
    }
    lineas.push("", indicacion);
    if (args.contacto)
        lineas.push("", `Contacto: ${args.contacto}`);
    lineas.push("", "Saludos,", firma);
    return {
        asunto,
        texto: lineas.join("\n"),
        titulo,
        situacion,
        datos,
        indicacion,
        pideConfirmacion,
        urgencia,
        firma,
        contacto: args.contacto,
    };
}
//# sourceMappingURL=dominio-web.js.map